import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, Download, Loader2 } from "lucide-react";

const LEVEL = { info: "bg-[#3A2DB8]/10 text-[#3A2DB8]", warning: "bg-[#FF8A3D]/15 text-[#B45309]", error: "bg-[#E5325F]/15 text-[#E5325F]" };

export default function AdminActivityLog() {
  const [data, setData] = useState({ items: [], kinds: [] });
  const [kind, setKind] = useState("all");
  const [level, setLevel] = useState("all");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (kind !== "all") params.kind = kind;
      if (level !== "all") params.level = level;
      if (email.trim()) params.email = email.trim();
      const { data } = await api.get("/admin/audit", { params });
      setData(data);
    } finally { setLoading(false); }
  }, [kind, level, email]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const exportCsv = () => {
    const rows = [["timestamp_utc", "kind", "level", "email", "actor", "ip", "details"]]
      .concat(data.items.map((i) => [i.ts, i.kind, i.level, i.email || "", i.actor || "", i.ip || "", JSON.stringify(i.meta || {})]));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `trinity-activity-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card-soft" data-testid="admin-activity">
      <div className="p-4 border-b border-border flex flex-wrap items-center gap-2">
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-56 rounded-lg" data-testid="activity-kind-select"><SelectValue placeholder="Event type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All event types</SelectItem>{data.kinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="w-36 rounded-lg" data-testid="activity-level-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All levels</SelectItem><SelectItem value="info">info</SelectItem><SelectItem value="warning">warning</SelectItem><SelectItem value="error">error</SelectItem></SelectContent>
        </Select>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Filter by customer email" className="w-64 rounded-lg" data-testid="activity-email-input" />
        <Button variant="outline" onClick={load} className="rounded-lg gap-1 border-border" data-testid="activity-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </Button>
        <Button variant="outline" onClick={exportCsv} className="rounded-lg gap-1 border-border ml-auto" data-testid="activity-export-btn"><Download className="w-4 h-4" /> CSV</Button>
      </div>
      <div className="max-h-[70vh] overflow-auto">
        <Table>
          <TableHeader><TableRow className="border-border hover:bg-transparent">
            <TableHead>Time (UTC)</TableHead><TableHead>Event</TableHead><TableHead>Who</TableHead><TableHead>IP</TableHead><TableHead>Details</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.map((i) => (
              <TableRow key={i.id} className="border-border" data-testid={`activity-row-${i.id}`}>
                <TableCell className="font-mono text-xs text-secondary whitespace-nowrap">{i.ts?.slice(0, 19).replace("T", " ")}</TableCell>
                <TableCell><Badge className={`rounded-full text-[10px] ${LEVEL[i.level] || LEVEL.info}`}>{i.kind}</Badge></TableCell>
                <TableCell className="text-xs">{i.email || <span className="text-secondary">system</span>}{i.actor && <div className="text-[10px] text-[#7C3AED]">via {i.actor}</div>}</TableCell>
                <TableCell className="font-mono text-xs text-secondary">{i.ip || "—"}</TableCell>
                <TableCell className="text-xs text-secondary font-mono max-w-[420px] truncate" title={JSON.stringify(i.meta)}>{Object.entries(i.meta || {}).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join("  ")}</TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-secondary py-8">No events match.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
